import {
  Button,
  Dialog,
  Field,
  Input,
  Label,
  Select,
  Spinner,
  Textarea,
} from "@enduring-systems/react-ui";
import { useEffect, useRef, useState } from "react";

import {
  type DebugSendBody,
  fetchNamespaceAddresses,
  type NamespaceAddress,
} from "./debug-send-api";
import type { MessageSummary } from "./messages-api";
import { useDebugSend } from "./use-debug-send";

const MAX_DEBUG_SEND_PER_MINUTE = 10;

function errorText(code: number | null, serverMessage: string | null): string {
  switch (code) {
    case 429:
      return `Rate limit reached — you can send at most ${MAX_DEBUG_SEND_PER_MINUTE} debug messages per minute. Try again shortly.`;
    case 401:
      return "Authentication required — sign in via the ops-console gate first.";
    case 403:
      return "Debug send is not available in production.";
    case 404:
      return "The selected address was not found. Refresh and try again.";
    case 400:
      return serverMessage ?? "Invalid request. Check the form fields and try again.";
    default:
      return "Send failed — check your connection and try again.";
  }
}

interface DebugSendModalProps {
  open: boolean;
  onClose: () => void;
  baseUrl: string;
  namespaceId: string;
  items: MessageSummary[];
  refresh: () => Promise<void>;
  onFound?: (messageId: string) => void;
}

export function DebugSendModal({
  open,
  onClose,
  baseUrl,
  namespaceId,
  items,
  refresh,
  onFound,
}: DebugSendModalProps): React.ReactElement {
  const [addresses, setAddresses] = useState<NamespaceAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressLabel, setAddressLabel] = useState("");
  const [subject, setSubject] = useState("Debug test message");
  const [htmlBody, setHtmlBody] = useState("");
  const [textBody, setTextBody] = useState("Debug test from Endure ops console.");

  const { status, foundMessageId, errorCode, errorMessage, send, reset } = useDebugSend({
    baseUrl,
    namespaceId,
    items,
    refresh,
  });

  // onFound callback is unstable across renders (parent passes inline arrows).
  // Hold the latest reference in a ref + gate firing by the message id we
  // already notified about, so the callback fires exactly once per polled
  // arrival even when the parent re-renders during the highlight fade.
  const onFoundRef = useRef(onFound);
  onFoundRef.current = onFound;
  const lastNotifiedRef = useRef<string | null>(null);
  useEffect(() => {
    if (status === "found" && foundMessageId && lastNotifiedRef.current !== foundMessageId) {
      lastNotifiedRef.current = foundMessageId;
      onFoundRef.current?.(foundMessageId);
    }
    if (status === "idle") {
      lastNotifiedRef.current = null;
    }
  }, [status, foundMessageId]);

  useEffect(() => {
    if (!open) {
      reset();
      setAddresses([]);
      setAddressLabel("");
      setSubject("Debug test message");
      setHtmlBody("");
      setTextBody("Debug test from Endure ops console.");
      return;
    }

    // Stale-response guard: if `open`/`baseUrl`/`namespaceId` flip while a
    // fetch is in flight, the cleanup below flips `stale` so the older
    // promise's resolve cannot clobber the new request's address list.
    let stale = false;
    setAddressesLoading(true);
    fetchNamespaceAddresses(baseUrl, namespaceId)
      .then((list) => {
        if (stale) return;
        setAddresses(list);
        const first = list[0];
        if (first) setAddressLabel(first.email);
      })
      .catch(() => {
        if (stale) return;
        setAddresses([]);
      })
      .finally(() => {
        if (stale) return;
        setAddressesLoading(false);
      });

    return (): void => {
      stale = true;
    };
  }, [open, baseUrl, namespaceId, reset]);

  const isSendDisabled =
    addressesLoading || addresses.length === 0 || status === "sending" || status === "polling";

  const handleSend = (): void => {
    const body: DebugSendBody = {};
    if (addressLabel) body.addressLabel = addressLabel;
    if (subject) body.subject = subject;
    if (htmlBody) body.htmlBody = htmlBody;
    if (textBody) body.textBody = textBody;
    void send(body);
  };

  const handleClose = (): void => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && handleClose()}
      data-testid="debug-send-modal"
    >
      <Dialog.Header>
        <Dialog.Title>Send test message</Dialog.Title>
        <Dialog.Description>
          Dispatches a synthetic email through the test-inbox pipeline and polls for arrival.
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Body>
        <div className="space-y-4">
          {addressesLoading && (
            <div
              className="flex items-center gap-2 text-sm text-muted-foreground"
              data-testid="addresses-loading"
            >
              <Spinner size="sm" />
              Loading addresses…
            </div>
          )}

          {!addressesLoading && addresses.length === 0 && (
            <p className="text-sm text-destructive" data-testid="no-addresses-message">
              No addresses in this namespace — create one first.
            </p>
          )}

          {!addressesLoading && addresses.length > 0 && (
            <Field>
              <Label htmlFor="debug-send-address">To address</Label>
              <Select
                id="debug-send-address"
                data-testid="address-select"
                value={addressLabel}
                onChange={(e) => setAddressLabel(e.target.value)}
              >
                {addresses.map((a) => (
                  <option key={a.id} value={a.email}>
                    {a.email}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field>
            <Label htmlFor="debug-send-subject">Subject</Label>
            <Input
              id="debug-send-subject"
              data-testid="subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
          </Field>

          <Field>
            <Label htmlFor="debug-send-text-body">Text body</Label>
            <Textarea
              id="debug-send-text-body"
              data-testid="text-body-input"
              value={textBody}
              onChange={(e) => setTextBody(e.target.value)}
              rows={3}
            />
          </Field>

          <Field>
            <Label htmlFor="debug-send-html-body">HTML body (optional)</Label>
            <Textarea
              id="debug-send-html-body"
              data-testid="html-body-input"
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.target.value)}
              rows={3}
            />
          </Field>

          {status === "sending" && (
            <div className="flex items-center gap-2 text-sm" data-testid="send-sending">
              <Spinner size="sm" />
              Sending…
            </div>
          )}

          {status === "polling" && (
            <div className="flex items-center gap-2 text-sm" data-testid="send-polling">
              <Spinner size="sm" />
              Waiting for message to arrive…
            </div>
          )}

          {status === "found" && foundMessageId && (
            <p className="text-sm text-success" data-testid="send-success">
              Message arrived. Highlighted in the list.
            </p>
          )}

          {status === "timed-out" && (
            <p className="text-sm text-muted-foreground" data-testid="send-timed-out">
              Message not detected after 60 s — it may still be in transit. Check the list or
              reload.
            </p>
          )}

          {status === "error" && (
            <p className="text-sm text-destructive" data-testid="send-error">
              {errorText(errorCode, errorMessage)}
            </p>
          )}
        </div>
      </Dialog.Body>

      <Dialog.Footer>
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSend}
          disabled={isSendDisabled}
          data-testid="send-button"
        >
          {status === "sending" ? "Sending…" : "Send"}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
